/* eslint-disable no-fallthrough */
import axios from 'axios';
import { request } from '@wix/da-shared-react/pkg/xhr';
import snakeCase from 'lodash/snakeCase';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import { getEnabledFlagsList } from '@wix/da-shared-react/pkg/redux/flags/selectors';

import { API_BASE_PATH } from '../../constants';
import {
  fetchModuleConfig,
  fetchModuleConfigSuccess,
  saveModuleConfig,
  updateModule,
} from '../actions/modules';
import { getModuleType, getModuleConfig } from '../selectors/modules';
import { getProfileOwnerUser } from '../selectors/users';
import { getModuleInitActions } from '../store/initActions/modules';
import { ConfigSelectors } from '@wix/da-shared-react/pkg/config';
import { setCsrfOnParams } from './streams';

function* postModuleEndpoint(endpoint, params) {
  return yield call(request, {
    method: 'post',
    baseURL: API_BASE_PATH,
    url: `/module/${endpoint}`,
    data: transformParamNames(params),
  });
}

export function* runModuleInitActions(moduleResponse, requestParams = {}) {
  const flags = yield select(getEnabledFlagsList);
  const actions = getModuleInitActions(moduleResponse, requestParams, flags);
  if (!actions || !actions.length) {
    // No module actions found
    return;
  }
  // dispatch actions
  yield all(actions.map(moduleAction => put(moduleAction)));
}

function* getModuleEndpoint(endpoint, params) {
  params = yield setCsrfOnParams(params);
  return yield axios
    .get(`${API_BASE_PATH}/module/${endpoint}`, {
      params: transformParamNames(params),
    })
    .then(response => response.data)
    .catch(response => false);
}

function transformParamNames(params) {
  return Object.keys(params).reduce((transformedParams, key) => {
    switch (key) {
      case 'moduleId':
      case 'folderId':
      case 'countryId':
        // convert to lovercase
        transformedParams[key.toLowerCase()] = params[key];
        break;
      case 'sourceType':
      case 'viewMode':
      case 'slideImageFit':
      case 'sortBy':
      // members:
      case 'rolesFilter':
      // active_members
      case 'displayLimit':
      // custombox
      case 'backgroundType':
      case 'imageDisplayMode':
      case 'imageDeviationid':
      case 'imageTint':
      // about module
      case 'dobDay':
      case 'dobMonth':
      case 'dobYear':
      case 'websiteLabel':
      case 'showBadges':
      case 'socialLinks':
      case 'isArtist':
        // convert to snake_case
        transformedParams[snakeCase(key)] = params[key];
        break;
      default:
        transformedParams[key] = params[key];
    }
    return transformedParams;
  }, {});
}

export function* saveModuleConfigSaga(action) {
  const {
    payload: { _endpoint, ...payload },
  } = action;
  const { moduleId } = payload;
  yield put(
    updateModule({ id: moduleId, moduleMeta: { isSavingConfig: true } })
  );

  const { username } = yield select(getProfileOwnerUser);
  const moduleType = yield select(getModuleType, payload.moduleId);
  const { config = {} } = yield select(getModuleConfig, payload.moduleId);
  const { version = payload.version } = config;
  const csrf = yield select(ConfigSelectors.key('csrfToken'));

  const response = yield call(
    postModuleEndpoint,
    _endpoint || `${moduleType}/save`,
    {
      ...payload,
      username,
      version,
      csrf_token: csrf,
    }
  );
  yield put(
    updateModule({ id: moduleId, moduleMeta: { isSavingConfig: false } })
  );

  // Basic error handling
  if (!response || response.error) {
    if (response.errorDescription) {
      alert(`Error: ${response.errorDescription}`);
      return;
    }
    alert('Failed to save, please try again...');
    return;
  }

  if (response) {
    yield runModuleInitActions(response);
  }
}

export function* fetchModuleConfigSaga(action) {
  const { payload } = action;
  const { moduleId } = payload;
  const moduleType = yield select(getModuleType, payload.moduleId);
  const { username } = yield select(getProfileOwnerUser);

  yield put(
    updateModule({ id: moduleId, moduleMeta: { isFetchingConfig: true } })
  );

  const csrf = yield select(ConfigSelectors.key('csrfToken'));

  const response = yield call(getModuleEndpoint, `${moduleType}/config`, {
    ...payload,
    csrf_token: csrf,
    username,
  });

  if (response) {
    // update module data in the store
    yield put(
      fetchModuleConfigSuccess({ id: payload.moduleId, config: response })
    );
  }

  yield put(
    updateModule({ id: moduleId, moduleMeta: { isFetchingConfig: false } })
  );
}

export default function* rootModulesSaga() {
  yield all([
    takeEvery(saveModuleConfig, saveModuleConfigSaga),
    takeEvery(fetchModuleConfig, fetchModuleConfigSaga),
  ]);
}
